import { onlineManager, useQuery } from '@tanstack/react-query';

import { ILocationSuggest } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';
import { germanyCenter } from '@mopla/utils';

import { useBusinessLayer } from '../../business-logic';

import { getLocations } from './useLocationSuggestions';

const SUGGESTIONS_STALE_TIME_MS = 5 * 60 * 1000;

export const locationSuggestionsKeys = {
	all: ['locationSuggestions'] as const,
	query: (query: string, position?: string | null) =>
		['locationSuggestions', query, position ?? ''] as const,
};

/** React Query variant of useLocationSuggestions. Caches per query+position, no flicker on load. */
export const useLocationSuggestionsQuery = (
	query: string,
	position?: string | null
): { suggestions: ILocationSuggest[]; isLoading: boolean } => {
	const businessLayer = useBusinessLayer();
	const logger = useLogger('useLocationSuggestionsQuery');
	const trimmedQuery = query.trim();
	const enabled = trimmedQuery.length > 0;

	const { data, isFetching } = useQuery({
		queryKey: locationSuggestionsKeys.query(trimmedQuery, position),
		queryFn: async () => {
			try {
				return await getLocations(
					trimmedQuery,
					businessLayer,
					position || germanyCenter.toString()
				);
			} catch (error) {
				logger.error('Failed to fetch location suggestions', error);
				throw error;
			}
		},
		enabled,
		/** Keep prev results while loading (no flicker), but only online — offline req fails, stale would flash. */
		placeholderData: (previousData) =>
			onlineManager.isOnline() ? previousData : undefined,
		staleTime: SUGGESTIONS_STALE_TIME_MS,
		/** 'always': default 'online' pauses offline req (queryFn never runs) → list never clears. */
		networkMode: 'always',
		/** No retry: fail fast, clear list. */
		retry: false,
	});

	return {
		suggestions: enabled ? data ?? [] : [],
		isLoading: enabled && isFetching,
	};
};
